import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
  untracked,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { RippleModule } from "primeng/ripple";
import { SelectButtonModule } from "primeng/selectbutton";
import { LayoutStore } from "@/app/core/state/layout.store";
import { Theme } from "@/app/layout/models";

interface ThemeOption {
  label: string;
  value: Theme;
}

/**
 * Theme switcher component allowing users to select and change the application theme.
 */
@Component({
  selector: "app-theme-switcher",
  imports: [SelectButtonModule, RippleModule, FormsModule],
  templateUrl: "./theme-switcher.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThemeSwitcherComponent {
  private readonly translateService = inject(TranslateService);
  private readonly layoutStore = inject(LayoutStore);

  readonly themeOptions = signal<ThemeOption[]>([]);
  readonly selectedTheme = signal<ThemeOption | null>(null);

  /**
   * Initializes the component and sets up reactive effects.
   */
  constructor() {
    // Reactive effect triggered when translations are loaded.
    effect(() => {
      // Generate the list of available theme options dynamically from the Theme enum.
      const options = Object.values(Theme).map((theme) => ({
        label: this.translateService.instant(`themeOption.${theme.toLowerCase()}`),
        value: theme,
      }));
      this.themeOptions.set(options);

      // Synchronise the selected theme with the current value of the LayoutStore.
      const selectedThemeOption = untracked(
        () => this.themeOptions().find((opt) => opt.value === this.layoutStore.theme()) ?? null,
      );
      this.selectedTheme.set(selectedThemeOption);
    });
  }

  /**
   * Handle theme selection changes.
   *
   * @param {Theme} theme - The new selected theme.
   */
  onThemeChange(theme: Theme): void {
    this.selectedTheme.set(this.themeOptions().find((opt) => opt.value === theme) ?? null);
    this.layoutStore.setTheme(theme);
  }
}
